import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import RichTextEditor from '../../components/ui/RichTextEditor';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Checkbox } from '../../components/ui/Checkbox';
import { useUserAccess } from '../../context/UserAccessContext';
import {
  fetchHubupGroupTopics,
  fetchHubupGroup,
  patchHubupGroupJoinWelcome,
  patchHubupGroupAnnuary,
  createHubupGroupTopic,
  updateHubupTopic,
  deleteHubupTopic,
  fetchHubupGroupFormAnnuaire,
  uploadHubupCommunityFavicon,
  deleteHubupCommunityFavicon,
  fetchHubupCommunityFaviconBlobUrl,
  fetchHubupGroupAr,
  publishHubupGroupAr,
  deleteHubupGroupAr,
  resolveHubupAssetUrl,
} from '../../api/hubup';
import HubupPremiumQr from '../../components/hubup/HubupPremiumQr';
import HubupFormAnnuaireRecipientModal from '../../components/hubup/HubupFormAnnuaireRecipientModal';
import {
  HUBUP_ANNUARY_CATEGORIES,
  annuaryCategorySelectClass,
  isKnownAnnuaryCategory,
} from '../../constants/hubupAnnuaryCategories';
import {
  canManageHubupCommunitySettings,
  hasHubupCommunityAccess,
  isHubupPlatformAdmin,
} from '../../utils/hubupAccess';

const DESC_PREVIEW_LEN = 160;

function isHubupEditorTopic(t) {
  return String(t?.topicKind || '').trim() === 'editor';
}

function isHubupPrivateTopic(t) {
  return t?.private === true || String(t?.topicKind || '').trim() === 'private';
}

function extractTopicVisitorIds(t) {
  const raw = t?.visitorIds ?? t?.allowedVisitorIds;
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x).trim()).filter(Boolean);
}

function stripHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function topicDescriptionEditorInitialHtml(topic) {
  const h = (topic.descriptionHtml || '').trim();
  if (h) return h;
  const plain = (topic.description || '').trim();
  if (!plain) return '<p></p>';
  const esc = plain
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${esc.replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
}

const HubupSubjects = () => {
  const { groupId } = useParams();
  const { user } = useUserAccess();
  const isAdmin = isHubupPlatformAdmin(user);
  const canHubupManager = canManageHubupCommunitySettings(user);
  const canManageHubup = hasHubupCommunityAccess(user);

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDesc, setExpandedDesc] = useState(() => new Set());
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formDescHtml, setFormDescHtml] = useState('<p></p>');
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [annuaireContacts, setAnnuaireContacts] = useState([]);
  const [topicIsPrivate, setTopicIsPrivate] = useState(false);
  const [topicVisitorIds, setTopicVisitorIds] = useState([]);
  const [topicHadPrivateOnOpen, setTopicHadPrivateOnOpen] = useState(false);
  const [topicRecipientModalOpen, setTopicRecipientModalOpen] = useState(false);

  const [groupWelcome, setGroupWelcome] = useState({
    subject: '',
    html: '',
    configured: false,
  });
  const [groupWelcomeLoadError, setGroupWelcomeLoadError] = useState(null);
  const [welcomeModal, setWelcomeModal] = useState(false);
  const [welcomeFormSubject, setWelcomeFormSubject] = useState('');
  const [welcomeFormHtml, setWelcomeFormHtml] = useState('<p></p>');
  const [welcomeSaving, setWelcomeSaving] = useState(false);
  const [welcomeFormError, setWelcomeFormError] = useState(null);

  const [annuaryEnabled, setAnnuaryEnabled] = useState(false);
  const [annuaryCategory, setAnnuaryCategory] = useState('');
  const [annuaryLine1, setAnnuaryLine1] = useState('');
  const [annuaryLine2, setAnnuaryLine2] = useState('');
  const [annuaryLine3, setAnnuaryLine3] = useState('');
  const [annuarySaving, setAnnuarySaving] = useState(false);
  const [annuaryError, setAnnuaryError] = useState(null);
  const [annuaryLoadError, setAnnuaryLoadError] = useState(null);

  const [faviconUrl, setFaviconUrl] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [faviconCacheBust, setFaviconCacheBust] = useState(null);
  const [faviconObjectPreview, setFaviconObjectPreview] = useState('');
  const [faviconBusy, setFaviconBusy] = useState(false);
  const [faviconError, setFaviconError] = useState(null);
  const faviconInputRef = useRef(null);

  const [qrRaPanelOpen, setQrRaPanelOpen] = useState(false);

  const [arConfig, setArConfig] = useState(null);
  const [arDisplayFile, setArDisplayFile] = useState(null);
  const [arUrl, setArUrl] = useState('');
  const [arDisplayPreview, setArDisplayPreview] = useState('');
  const [arBusy, setArBusy] = useState(false);
  const [arError, setArError] = useState(null);
  const [arLoadError, setArLoadError] = useState(null);
  const arDisplayInputRef = useRef(null);

  const topicsListed = useMemo(
    () =>
      isAdmin
        ? topics
        : topics.filter((t) => !isHubupEditorTopic(t)),
    [topics, isAdmin],
  );

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    setGroupWelcomeLoadError(null);
    try {
      const res = await fetchHubupGroupTopics(groupId);
      setTopics(res?.data?.topics || []);
    } catch (e) {
      setError('Impossible de charger les rubriques.');
      setTopics([]);
    } finally {
      setLoading(false);
    }
    if (canHubupManager) {
      try {
        const grpRes = await fetchHubupGroup(groupId);
        const g = grpRes?.data?.group;
        if (g) {
          setGroupWelcome({
            subject: String(g.joinWelcomeSubject ?? ''),
            html: String(g.joinWelcomeHtml ?? ''),
            configured: grpRes?.data?.joinWelcomeConfigured === true,
          });
          setAnnuaryEnabled(g.annuary === true);
          setAnnuaryCategory(String(g.category ?? ''));
          setAnnuaryLine1(String(g.line1 ?? ''));
          setAnnuaryLine2(String(g.line2 ?? ''));
          setAnnuaryLine3(String(g.line3 ?? ''));
          setAnnuaryLoadError(null);
          setFaviconUrl(String(g.faviconUrl ?? '').trim());
          setInviteCode(String(g.inviteCode ?? '').trim());
        } else {
          setGroupWelcome({ subject: '', html: '', configured: false });
          setGroupWelcomeLoadError('Détail communauté indisponible.');
          setAnnuaryLoadError('Détail communauté indisponible.');
        }
      } catch {
        setGroupWelcomeLoadError(
          'Impossible de charger le message de bienvenue (adhésion).',
        );
        setAnnuaryLoadError('Impossible de charger les paramètres annuaire.');
      }
      try {
        const arRes = await fetchHubupGroupAr(groupId);
        const ar = arRes?.data?.ar;
        setArConfig(ar || null);
        setArUrl(ar?.url ? String(ar.url) : '');
        setArLoadError(null);
        setArDisplayPreview(
          ar?.displayAssetUrl
            ? resolveHubupAssetUrl(ar.displayAssetUrl)
            : '',
        );
      } catch {
        setArConfig(null);
        setArLoadError('Impossible de charger le scanner RA.');
      }
    }
  }, [groupId, canHubupManager]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!groupId) {
      setAnnuaireContacts([]);
      return undefined;
    }
    let cancelled = false;
    void fetchHubupGroupFormAnnuaire(groupId)
      .then((res) => {
        if (!cancelled) {
          setAnnuaireContacts(
            Array.isArray(res?.data?.items) ? res.data.items : [],
          );
        }
      })
      .catch(() => {
        if (!cancelled) setAnnuaireContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const resetTopicPrivateState = () => {
    setTopicIsPrivate(false);
    setTopicVisitorIds([]);
    setTopicHadPrivateOnOpen(false);
    setTopicRecipientModalOpen(false);
  };

  const applyTopicVisitorsFromRow = (t) => {
    const ids = extractTopicVisitorIds(t);
    setTopicVisitorIds(ids);
    setTopicIsPrivate(isHubupPrivateTopic(t));
    setTopicHadPrivateOnOpen(isHubupPrivateTopic(t));
    setTopicRecipientModalOpen(false);
  };

  const topicPrivateCheckboxDisabled =
    editTopic != null && isHubupEditorTopic(editTopic);

  const showTopicAnnuairePicker =
    (creatingTopic || editTopic) && !topicPrivateCheckboxDisabled;

  useEffect(() => {
    if (!groupId || !faviconUrl) {
      setFaviconObjectPreview((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
      return undefined;
    }
    let cancelled = false;
    let objectUrl = '';
    (async () => {
      const url = await fetchHubupCommunityFaviconBlobUrl(
        groupId,
        faviconCacheBust ?? undefined,
      );
      if (cancelled) {
        if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url || '';
      setFaviconObjectPreview((prev) => {
        if (prev?.startsWith('blob:') && prev !== objectUrl) {
          URL.revokeObjectURL(prev);
        }
        return objectUrl;
      });
    })();
    return () => {
      cancelled = true;
      if (objectUrl?.startsWith('blob:')) URL.revokeObjectURL(objectUrl);
    };
  }, [groupId, faviconUrl, faviconCacheBust]);

  const toggleDesc = (id) => {
    setExpandedDesc((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setCreatingTopic(true);
    setEditTopic(null);
    setFormTitle('');
    setFormCategory('');
    setFormDescHtml('<p></p>');
    setFormError(null);
    resetTopicPrivateState();
  };

  const openEdit = (t) => {
    setCreatingTopic(false);
    setEditTopic(t);
    setFormTitle(t.title);
    setFormCategory(t.category || '');
    setFormDescHtml(topicDescriptionEditorInitialHtml(t));
    setFormError(null);
    applyTopicVisitorsFromRow(t);
  };

  const closeTopicModal = () => {
    setCreatingTopic(false);
    setEditTopic(null);
    setFormError(null);
    resetTopicPrivateState();
  };

  const openWelcomeModal = () => {
    setWelcomeFormSubject(groupWelcome.subject || '');
    const h = (groupWelcome.html || '').trim();
    setWelcomeFormHtml(h || '<p></p>');
    setWelcomeFormError(null);
    setWelcomeModal(true);
  };

  const closeWelcomeModal = () => {
    setWelcomeModal(false);
    setWelcomeFormError(null);
  };

  const handleSaveWelcome = async (e) => {
    e?.preventDefault();
    if (!groupId) return;
    const subj = welcomeFormSubject.trim();
    const plain = stripHtml(welcomeFormHtml || '');
    if (!subj || !plain) {
      setWelcomeFormError(
        'Renseignez un titre et un message (le corps ne peut pas être vide).',
      );
      return;
    }
    setWelcomeFormError(null);
    setWelcomeSaving(true);
    try {
      const res = await patchHubupGroupJoinWelcome(groupId, {
        joinWelcomeSubject: subj,
        joinWelcomeHtml: welcomeFormHtml || '',
      });
      const g = res?.data?.group;
      if (g) {
        setGroupWelcome({
          subject: String(g.joinWelcomeSubject ?? ''),
          html: String(g.joinWelcomeHtml ?? ''),
          configured: res?.data?.joinWelcomeConfigured === true,
        });
      }
      closeWelcomeModal();
      const again = await fetchHubupGroup(groupId);
      const g2 = again?.data?.group;
      if (g2) {
        setGroupWelcome({
          subject: String(g2.joinWelcomeSubject ?? ''),
          html: String(g2.joinWelcomeHtml ?? ''),
          configured: again?.data?.joinWelcomeConfigured === true,
        });
      }
    } catch (err) {
      setWelcomeFormError('Enregistrement impossible.');
    } finally {
      setWelcomeSaving(false);
    }
  };

  const handleSaveAnnuary = async (e) => {
    e?.preventDefault();
    if (!groupId || !canHubupManager) return;
    if (annuaryEnabled) {
      const category = annuaryCategory.trim();
      const l1 = annuaryLine1.trim();
      const l2 = annuaryLine2.trim();
      const l3 = annuaryLine3.trim();
      if (!category || !l1 || !l2 || !l3) {
        setAnnuaryError(
          'La catégorie et les trois lignes de description sont obligatoires.',
        );
        return;
      }
      if (!isKnownAnnuaryCategory(category)) {
        setAnnuaryError('Choisissez une catégorie dans la liste.');
        return;
      }
    }
    setAnnuaryError(null);
    setAnnuarySaving(true);
    try {
      const res = await patchHubupGroupAnnuary(groupId, {
        annuary: annuaryEnabled,
        category: annuaryEnabled ? annuaryCategory.trim() : '',
        line1: annuaryEnabled ? annuaryLine1.trim() : '',
        line2: annuaryEnabled ? annuaryLine2.trim() : '',
        line3: annuaryEnabled ? annuaryLine3.trim() : '',
      });
      const g = res?.data?.group;
      if (g) {
        setAnnuaryEnabled(g.annuary === true);
        setAnnuaryCategory(String(g.category ?? ''));
        setAnnuaryLine1(String(g.line1 ?? ''));
        setAnnuaryLine2(String(g.line2 ?? ''));
        setAnnuaryLine3(String(g.line3 ?? ''));
      }
    } catch (err) {
      setAnnuaryError('Enregistrement impossible.');
    } finally {
      setAnnuarySaving(false);
    }
  };

  const handleSaveTopic = async (e) => {
    e?.preventDefault();
    const title = formTitle.trim();
    if (!title) {
      setFormError('Le titre est requis.');
      return;
    }
    if (topicIsPrivate && topicVisitorIds.length === 0) {
      setFormError('Choisissez au moins une personne dans l’annuaire.');
      setTopicRecipientModalOpen(true);
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        title,
        category: formCategory.trim(),
        descriptionHtml: formDescHtml || '',
      };
      if (topicIsPrivate) {
        payload.private = true;
        payload.visitorIds = topicVisitorIds;
      } else if (editTopic && topicHadPrivateOnOpen) {
        payload.private = false;
      }
      if (editTopic) {
        await updateHubupTopic(editTopic.id, payload);
      } else if (creatingTopic && groupId) {
        await createHubupGroupTopic(groupId, payload);
      } else {
        setFormError('Communauté introuvable.');
        setSaving(false);
        return;
      }
      closeTopicModal();
      await load();
    } catch (err) {
      setFormError(
        editTopic ? 'Enregistrement impossible.' : 'Création impossible.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFaviconFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !groupId || !canHubupManager) return;
    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) {
      setFaviconError('Image trop volumineuse (max 2 Mo).');
      return;
    }
    setFaviconError(null);
    const localPreview = URL.createObjectURL(file);
    setFaviconObjectPreview((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return localPreview;
    });
    setFaviconBusy(true);
    try {
      const res = await uploadHubupCommunityFavicon(groupId, file);
      const url = res?.data?.faviconUrl;
      if (url) setFaviconUrl(String(url));
      setFaviconCacheBust(Date.now());
    } catch {
      setFaviconError('Envoi impossible (PNG, JPEG, GIF ou WebP, max 2 Mo).');
    } finally {
      setFaviconBusy(false);
    }
  };

  const handleDeleteFavicon = async () => {
    if (!groupId || !canHubupManager || !faviconUrl) return;
    if (
      !window.confirm(
        'Retirer l’icône QR ? Le QR utilisera à nouveau l’icône de profil HubUP.',
      )
    ) {
      return;
    }
    setFaviconError(null);
    setFaviconBusy(true);
    try {
      await deleteHubupCommunityFavicon(groupId);
      setFaviconUrl('');
      setFaviconCacheBust(null);
      setFaviconObjectPreview((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    } catch {
      setFaviconError('Suppression impossible.');
    } finally {
      setFaviconBusy(false);
    }
  };

  const handleArDisplayFile = (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setArDisplayFile(file);
    setArError(null);
    const url = URL.createObjectURL(file);
    setArDisplayPreview((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
  };

  const handlePublishAr = async (ev) => {
    ev.preventDefault();
    if (!groupId || !canHubupManager) return;
    if (!arDisplayFile && !arConfig?.displayAssetUrl) {
      setArError('Choisissez une image ou une vidéo à afficher au scan.');
      return;
    }
    const link = arUrl.trim();
    if (link && !/^https?:\/\//i.test(link)) {
      setArError('Lien invalide (http ou https).');
      return;
    }
    setArError(null);
    setArBusy(true);
    try {
      const res = await publishHubupGroupAr(groupId, {
        file: arDisplayFile || undefined,
        url: link,
      });
      const ar = res?.data?.ar;
      if (ar) {
        setArConfig(ar);
        setArUrl(ar.url ? String(ar.url) : '');
        setArDisplayFile(null);
        if (ar.displayAssetUrl) {
          setArDisplayPreview(resolveHubupAssetUrl(ar.displayAssetUrl));
        }
      }
    } catch {
      setArError(
        'Enregistrement impossible. Vérifiez le fichier et réessayez dans quelques instants.',
      );
    } finally {
      setArBusy(false);
    }
  };

  const handleDeleteAr = async () => {
    if (!groupId || !canHubupManager) return;
    if (
      !window.confirm(
        'Retirer l’expérience au scan pour cette communauté ?',
      )
    ) {
      return;
    }
    setArBusy(true);
    setArError(null);
    try {
      await deleteHubupGroupAr(groupId);
      setArConfig(null);
      setArUrl('');
      setArDisplayFile(null);
      setArDisplayPreview('');
    } catch {
      setArError('Suppression impossible.');
    } finally {
      setArBusy(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Supprimer la rubrique « ${t.title} » ?`)) return;
    setError(null);
    try {
      await deleteHubupTopic(t.id);
      await load();
    } catch (err) {
      setError('Suppression impossible.');
    }
  };

  const colSpan = canManageHubup ? 4 : 3;

  return (
    <>
      <Helmet>
        <title>Mes rubriques — HubUP</title>
      </Helmet>
      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
            Mes rubriques
          </h1>
          {error ? (
            <p className="text-xs text-error mb-2">{error}</p>
          ) : null}
        </div>

        {canHubupManager ? (
        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden mb-6">
          <div className="p-4 md:p-6 border-b border-border flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Message de bienvenue à l’adhésion
              </h2>
              {groupWelcomeLoadError ? (
                <p className="text-xs text-error mb-2">{groupWelcomeLoadError}</p>
              ) : null}
              {!groupWelcome.configured ? (
                <p className="text-xs font-medium text-amber-600 dark:text-amber-500 mb-2">
                  Non configuré — à définir pour débloquer les publications depuis « Mes notifications ».
                </p>
              ) : null}
              <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {groupWelcome.subject?.trim()
                    ? groupWelcome.subject
                    : '— (aucun titre)'}
                </p>
                {stripHtml(groupWelcome.html || '') ? (
                  <div
                    className="text-sm prose prose-sm max-w-none text-muted-foreground dark:prose-invert [&_p]:my-1 [&_a]:text-primary"
                    dangerouslySetInnerHTML={{
                      __html: groupWelcome.html || '',
                    }}
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">Aucun texte.</p>
                )}
              </div>
            </div>
              <Button
                type="button"
                variant="outline"
                onClick={openWelcomeModal}
                iconName="Pencil"
                iconPosition="left"
                className="w-full sm:w-auto shrink-0"
              >
                Modifier
              </Button>
          </div>
        </div>
        ) : null}

        {canHubupManager ? (
          <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden mb-6">
            <button
              type="button"
              className="w-full p-4 md:px-6 md:py-4 flex items-center justify-between gap-3 text-left border-b border-border hover:bg-muted/20 transition-smooth"
              onClick={() => setQrRaPanelOpen((o) => !o)}
              aria-expanded={qrRaPanelOpen}
            >
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground">
                  QR code &amp; scanner RA
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  Icône, QR code, contenu au scan
                  {arConfig?.active ? ' · actif dans l’app' : ''}
                </p>
              </div>
              <Icon
                name={qrRaPanelOpen ? 'ChevronUp' : 'ChevronDown'}
                size={20}
                className="shrink-0 text-muted-foreground"
              />
            </button>
            {qrRaPanelOpen ? (
              <div className="p-4 md:p-6 space-y-8 border-b border-border">
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4">
                    Icône au centre du QR
                  </h3>
                  <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="sr-only"
                        disabled={loading || faviconBusy}
                        onChange={(e) => void handleFaviconFile(e)}
                      />
                      <div
                        className={`relative h-20 w-20 rounded-xl border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0 ${
                          faviconBusy ? 'opacity-60' : ''
                        }`}
                      >
                        {faviconBusy ? (
                          <span className="text-[10px] text-muted-foreground">
                            …
                          </span>
                        ) : faviconObjectPreview ? (
                          <img
                            key={faviconObjectPreview}
                            src={faviconObjectPreview}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Icon
                            name="Image"
                            size={28}
                            className="text-muted-foreground"
                          />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          iconName="Upload"
                          iconPosition="left"
                          disabled={loading || faviconBusy}
                          onClick={() => faviconInputRef.current?.click()}
                        >
                          {faviconUrl
                            ? 'Changer l’icône QR'
                            : 'Ajouter une icône QR'}
                        </Button>
                        {faviconUrl && !faviconBusy ? (
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-error justify-start px-0 h-auto min-h-0"
                            onClick={() => void handleDeleteFavicon()}
                          >
                            Retirer
                          </Button>
                        ) : null}
                        {faviconError ? (
                          <p className="text-xs text-error">{faviconError}</p>
                        ) : null}
                      </div>
                    </div>
                    {inviteCode ? (
                      <div className="flex flex-col items-center gap-2 lg:ml-auto">
                        <p className="text-[11px] text-muted-foreground">
                          Aperçu du QR code
                        </p>
                        <div className="rounded-lg border border-border bg-background p-3">
                          <HubupPremiumQr
                            groupId={groupId}
                            inviteCode={inviteCode}
                            faviconUrl={faviconUrl}
                            size={140}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <form
                  onSubmit={(e) => void handlePublishAr(e)}
                  className="space-y-4 pt-6 border-t border-border"
                >
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">
                      Contenu affiché au scan
                    </h3>
                    {arLoadError ? (
                      <p className="text-xs text-error mt-2">{arLoadError}</p>
                    ) : null}
                    {arConfig?.active ? (
                      <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-2">
                        Disponible dans l&apos;app HubUP.
                      </p>
                    ) : arConfig?.displayAssetUrl ||
                      arConfig?.targetImageUrl ? (
                      <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">
                        Mise en ligne en cours — réessayez l&apos;enregistrement
                        dans un instant si besoin.
                      </p>
                    ) : null}
                  </div>
                  <input
                    ref={arDisplayInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
                    className="sr-only"
                    disabled={arBusy || loading}
                    onChange={handleArDisplayFile}
                  />
                  <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                    {arDisplayPreview ? (
                      <div className="rounded-lg border border-border overflow-hidden max-w-[200px]">
                        {arDisplayFile?.type?.startsWith('video/') ||
                        arConfig?.displayKind === 'video' ? (
                          <video
                            src={arDisplayPreview}
                            className="max-h-40 w-full object-contain bg-black"
                            controls
                            muted
                          />
                        ) : (
                          <img
                            src={arDisplayPreview}
                            alt=""
                            className="max-h-40 w-full object-contain bg-muted/30"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="h-24 w-24 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground">
                        <Icon name="Sparkles" size={24} />
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        iconName="Upload"
                        iconPosition="left"
                        disabled={arBusy || loading}
                        onClick={() => arDisplayInputRef.current?.click()}
                      >
                        {arConfig?.displayAssetUrl || arDisplayFile
                          ? 'Changer le contenu'
                          : 'Choisir image ou vidéo'}
                      </Button>
                      <p className="text-[11px] text-muted-foreground max-w-xs">
                        Max ~50 Mo. PNG/JPEG/WebP/GIF ou MP4/WebM/MOV.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="hubup-ar-url"
                      className="text-xs font-medium text-foreground block mb-1"
                    >
                      Lien
                    </label>
                    <input
                      id="hubup-ar-url"
                      type="url"
                      inputMode="url"
                      autoComplete="off"
                      placeholder="https://"
                      value={arUrl}
                      onChange={(e) => setArUrl(e.target.value)}
                      disabled={arBusy || loading}
                      className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  {arError ? (
                    <p className="text-xs text-error">{arError}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      variant="success"
                      iconName="Save"
                      iconPosition="left"
                      disabled={arBusy || loading}
                    >
                      {arBusy
                        ? 'Traitement et envoi en cours…'
                        : 'Enregistrer pour l’app'}
                    </Button>
                    {arConfig?.targetImageUrl ||
                    arConfig?.displayAssetUrl ||
                    arConfig?.active ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={arBusy || loading}
                        onClick={() => void handleDeleteAr()}
                      >
                        Désactiver l’expérience
                      </Button>
                    ) : null}
                  </div>
                </form>
              </div>
            ) : null}
          </div>
        ) : null}

        {canHubupManager ? (
        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden mb-6">
          <div className="p-4 md:p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Publication dans l&apos;annuaire
            </h2>
            {annuaryLoadError ? (
              <p className="text-xs text-error mb-3">{annuaryLoadError}</p>
            ) : null}
              <form onSubmit={handleSaveAnnuary} className="space-y-4">
                <Checkbox
                  checked={annuaryEnabled}
                  onChange={(e) => setAnnuaryEnabled(e.target.checked)}
                  label="Voulez-vous être publié dans l'annuaire ?"
                />
                {annuaryEnabled ? (
                  <div className="space-y-3 pl-0 sm:pl-1">
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        Catégorie <span className="text-error">*</span>
                      </label>
                      <select
                        required
                        className={annuaryCategorySelectClass}
                        value={annuaryCategory}
                        onChange={(e) => setAnnuaryCategory(e.target.value)}
                      >
                        <option value="">Choisir une catégorie…</option>
                        {annuaryCategory &&
                        !isKnownAnnuaryCategory(annuaryCategory) ? (
                          <option value={annuaryCategory}>{annuaryCategory}</option>
                        ) : null}
                        {HUBUP_ANNUARY_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        Fonctionnalité principale <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={200}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        value={annuaryLine1}
                        onChange={(e) => setAnnuaryLine1(e.target.value)}
                        placeholder="Ex. Communauté des professionnels du vin"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        Bénéfice clé<span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={200}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        value={annuaryLine2}
                        onChange={(e) => setAnnuaryLine2(e.target.value)}
                        placeholder="Ex. Annonces, événements et actualités"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">
                        Différenciateur unique <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={200}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        value={annuaryLine3}
                        onChange={(e) => setAnnuaryLine3(e.target.value)}
                        placeholder="Ex. Région Champagne — Grand Est"
                      />
                    </div>
                  </div>
                ) : null}
                {annuaryError ? (
                  <p className="text-xs text-error">{annuaryError}</p>
                ) : null}
                <div className="flex justify-end pt-2">
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={annuarySaving}
                    iconName="Save"
                    iconPosition="left"
                  >
                    {annuarySaving ? 'Enregistrement…' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
          </div>
        </div>
        ) : null}

        <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
          <div className="p-4 md:p-6 border-b border-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Liste des rubriques
              </h2>
              <p className="text-xs text-muted-foreground">
                {topicsListed.length} rubrique(s)
              </p>
            </div>
            {canManageHubup ? (
              <Button
                type="button"
                onClick={openCreate}
                iconName="Plus"
                iconPosition="left"
                size="lg"
                className="w-full sm:w-auto shrink-0"
              >
                Nouvelle rubrique
              </Button>
            ) : null}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-36">
                    TAGS
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[14rem]">
                    Description
                  </th>
                  {canManageHubup ? (
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                    >
                      Chargement…
                    </td>
                  </tr>
                ) : topicsListed.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="px-4 py-6 text-center text-sm text-muted-foreground"
                    >
                      Aucune rubrique pour cette communauté.
                    </td>
                  </tr>
                ) : (
                  topicsListed.map((t) => {
                    const htmlRaw = (t.descriptionHtml || '').trim();
                    const plainField = (t.description || '').trim();
                    const displayPlain = plainField || stripHtml(htmlRaw);
                    const isLong = displayPlain.length > DESC_PREVIEW_LEN;
                    const expanded = expandedDesc.has(t.id);

                    return (
                      <tr key={t.id} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-4 py-3 text-sm text-foreground font-medium align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{t.title}</span>
                            {isHubupPrivateTopic(t) ? (
                              <span
                                className="text-[10px] font-semibold uppercase text-violet-800 bg-violet-100 dark:bg-violet-950/50 dark:text-violet-200 px-2 py-0.5 rounded-full"
                                title={
                                  extractTopicVisitorIds(t).length === 1
                                    ? 'Rubrique privée — 1 personne autorisée'
                                    : `Rubrique privée — ${extractTopicVisitorIds(t).length} personnes`
                                }
                              >
                                Privée
                                {extractTopicVisitorIds(t).length > 0
                                  ? ` · ${extractTopicVisitorIds(t).length}`
                                  : ''}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground align-top">
                          {t.category || '—'}
                        </td>
                        <td className="px-4 py-3 max-w-md align-top">
                          {!displayPlain && !htmlRaw ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : expanded ? (
                            <div>
                              {htmlRaw ? (
                                <div
                                  className="prose prose-sm max-w-none text-foreground dark:prose-invert [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-primary [&_strong]:font-semibold"
                                  dangerouslySetInnerHTML={{ __html: htmlRaw }}
                                />
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{displayPlain}</p>
                              )}
                              {isLong ? (
                                <button
                                  type="button"
                                  className="text-xs text-primary hover:underline mt-2"
                                  onClick={() => toggleDesc(t.id)}
                                >
                                  Voir moins
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-muted-foreground">
                                {isLong
                                  ? `${displayPlain.slice(0, DESC_PREVIEW_LEN)}…`
                                  : displayPlain}
                              </p>
                              {isLong ? (
                                <button
                                  type="button"
                                  className="text-xs text-primary hover:underline mt-1"
                                  onClick={() => toggleDesc(t.id)}
                                >
                                  Voir plus
                                </button>
                              ) : null}
                            </div>
                          )}
                        </td>
                        {canManageHubup ? (
                          <td className="px-4 py-3 text-right align-top">
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Modifier"
                                onClick={() => openEdit(t)}
                              >
                                <Icon
                                  name="Pencil"
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 rounded hover:bg-muted transition-smooth"
                                title="Supprimer"
                                onClick={() => handleDelete(t)}
                              >
                                <Icon name="Trash2" size={16} className="text-error" />
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {editTopic || creatingTopic ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-border"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">
                {creatingTopic ? 'Nouvelle rubrique' : 'Modifier la rubrique'}
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth"
                onClick={closeTopicModal}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleSaveTopic} className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Titre
                </label>
                <input
                  type="text"
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formTitle}
                  onChange={(ev) => setFormTitle(ev.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  TAGS
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formCategory}
                  onChange={(ev) => setFormCategory(ev.target.value)}
                  placeholder="Ex. Général"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Description
                </label>
                <RichTextEditor
                  value={formDescHtml}
                  onChange={setFormDescHtml}
                />
              </div>
              {topicPrivateCheckboxDisabled ? (
                <p className="text-xs text-muted-foreground">
                  La rubrique Éditeur ne peut pas être privée.
                </p>
              ) : showTopicAnnuairePicker ? (
                <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Accès à la rubrique
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Rubrique privée : seules les personnes choisies voient le fil
                      et sont abonnées. Les nouveaux membres ne sont pas ajoutés
                      automatiquement.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={topicIsPrivate}
                      disabled={topicPrivateCheckboxDisabled}
                      onChange={(ev) => {
                        const on = ev.target.checked;
                        setTopicIsPrivate(on);
                        if (!on) {
                          setTopicVisitorIds([]);
                        } else {
                          setTopicRecipientModalOpen(true);
                        }
                      }}
                      className="rounded border-input shrink-0"
                    />
                    <span>Rubrique privée</span>
                  </label>
                  {topicIsPrivate ? (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <p className="text-sm text-foreground flex-1">
                        {topicVisitorIds.length === 0
                          ? 'Aucune personne sélectionnée.'
                          : `${topicVisitorIds.length} personne${topicVisitorIds.length !== 1 ? 's' : ''} autorisée${topicVisitorIds.length !== 1 ? 's' : ''}.`}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        iconName="Users"
                        iconPosition="left"
                        onClick={() => setTopicRecipientModalOpen(true)}
                      >
                        Choisir…
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {formError ? (
                <p className="text-xs text-error">{formError}</p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeTopicModal}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="success"
                  iconName="Save"
                  iconPosition="left"
                  disabled={saving}
                >
                  {saving
                    ? creatingTopic
                      ? 'Création…'
                      : 'Enregistrement…'
                    : creatingTopic
                      ? 'Créer'
                      : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <HubupFormAnnuaireRecipientModal
        open={topicRecipientModalOpen}
        contacts={annuaireContacts}
        selectedIds={topicVisitorIds}
        onClose={() => setTopicRecipientModalOpen(false)}
        onConfirm={(ids) => {
          setTopicVisitorIds(ids);
          setTopicIsPrivate(ids.length > 0);
          setTopicRecipientModalOpen(false);
        }}
      />

      {canHubupManager && welcomeModal ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-border"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">
                Message de bienvenue à l’adhésion
              </h2>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-smooth"
                onClick={closeWelcomeModal}
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleSaveWelcome} className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Titre de la notification push
                </label>
                <input
                  type="text"
                  required
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={welcomeFormSubject}
                  onChange={(ev) => setWelcomeFormSubject(ev.target.value)}
                  placeholder="Ex. Bienvenue !"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Message (HTML)
                </label>
                <RichTextEditor
                  value={welcomeFormHtml}
                  onChange={setWelcomeFormHtml}
                />
              </div>
              {welcomeFormError ? (
                <p className="text-xs text-error">{welcomeFormError}</p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeWelcomeModal}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="success"
                  iconName="Save"
                  iconPosition="left"
                  disabled={welcomeSaving}
                >
                  {welcomeSaving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HubupSubjects;
